import 'swiper/css';
import 'swiper/css/pagination';

import { useTheme } from '@emotion/react';
import {
  faArrowDown,
  faArrowUp,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';
import { Box, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import {
  ButtonExpand,
  ButtonMinus,
  ButtonPlus,
} from '»components/atoms/Buttons';
import { iff } from '»utils/code';
import { useIsTitleNegotiable, useNegModalContext } from '»utils/customHooks';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { getPercentageDecorator } from '»utils/endDecorator';
import { TrlFormattedMessagePlain } from '»utils/i18n';
import { displayNominalValue } from '»utils/nominalValue';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';
import { TrlFieldDefaultValue } from '»utils/trlFieldDefaultValue';

import {
  DataLabel,
  DataValue,
  FontAwesomeIconStyled,
  Wrapper,
  WrapperButtons,
  WrapperDataColumn,
  WrapperDataInfo,
  WrapperDataRow,
} from './TrlCardStockExchangeDet.styles';
import { TrlMainDetails } from './TrlMainDetails';

export const TrlCardStockExchangeDet = ({ ...props }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  const showSwiper = useMediaQuery(theme.breakpoints.between('lg', 'xl'));

  const selectedTitle = props.selectedItem;

  const { isFunds } = props;

  const { openInsertOrderModal } = useNegModalContext();

  const {
    instrument,
    symbol,
    exchange,
    securityExchange,
    securityType,
    variationPctg,
    country,
    exchangeDescr,
    currency,
    descr,
    securityDescription,
    lastPx,
    lastDateTime,
    totalVolumeTraded,
    nominalValue,
    lowPx,
    highPx,
    bidPx,
    askPx,
    priceRepresentation,
    quantityRepresentation,
    isFavourite,
    canTrade,
    roundPrice,
  } = selectedTitle;

  const isTitleNegotiable = useIsTitleNegotiable({
    isMarketPrimaryScreen: false,
    isFundsPrimaryScreen: false,
    canTrade,
  });

  const decimalDigits = getDecimalDigits(roundPrice);

  const endDecorator = getPercentageDecorator(priceRepresentation);

  const getMiddleLeftContent = () => (
    <WrapperDataRow isFirstVariationWrapper>
      <WrapperDataColumn>
        <WrapperDataInfo isVariation>
          <DataLabel>
            {TrlFormattedMessagePlain({
              id: 'TRLT_UI-QUOTE',
            })}
          </DataLabel>
          <DataValue isBigger>
            <TrlNumberFormatted
              value={lastPx}
              fontFamily="Roboto"
              endDecorator={endDecorator}
              decimalDigits={decimalDigits}
            />
          </DataValue>
        </WrapperDataInfo>
        <DataValue isOrder>
          {lastDateTime ? (
            <>
              <TrlDateFormatted date={lastDateTime} />
              {' | '}
              <TrlTimeFormatted
                value={lastDateTime}
                hour="numeric"
                minute="numeric"
                second="numeric"
              />
            </>
          ) : (
            <TrlFieldDefaultValue />
          )}
        </DataValue>
      </WrapperDataColumn>

      <WrapperDataColumn alignStart>
        {!isFunds && (
          <>
            <WrapperDataInfo isVariation alignStart>
              <DataLabel>
                {TrlFormattedMessagePlain({
                  id: 'TRLT_UI-VOLUME',
                })}
              </DataLabel>
              <DataValue>
                <TrlNumberFormatted
                  value={totalVolumeTraded}
                  fontFamily="Roboto"
                  decimalDigits={decimalDigits}
                />
              </DataValue>
            </WrapperDataInfo>
            <WrapperDataInfo isVariation alignStart>
              <DataLabel>
                {TrlFormattedMessagePlain({
                  id: 'TRLT_UI-NOMINAL_VALUE',
                })}
              </DataLabel>
              <DataValue>
                <TrlNumberFormatted
                  value={displayNominalValue(
                    quantityRepresentation,
                    priceRepresentation,
                    nominalValue,
                  )}
                  fontFamily="Roboto"
                  decimalDigits={decimalDigits}
                />
              </DataValue>
            </WrapperDataInfo>
          </>
        )}
      </WrapperDataColumn>
    </WrapperDataRow>
  );

  const getMiddleRightContent = () => (
    <WrapperDataRow isSecondVariationWrapper>
      <WrapperDataRow>
        <WrapperDataColumn>
          <WrapperDataInfo isVariation>
            <DataLabel sx={{ marginRight: '39px' }}>
              {TrlFormattedMessagePlain({
                id: 'TRLT_UI-VARIATION',
              })}
            </DataLabel>
            <Box>
              <DataValue isVariation value={variationPctg}>
                {variationPctg === 0 ? (
                  <>
                    <FormattedNumber
                      value={variationPctg}
                      maximumFractionDigits={decimalDigits}
                      minimumFractionDigits={decimalDigits}
                    />
                    %
                  </>
                ) : (
                  <TrlNumberFormatted
                    value={variationPctg}
                    endDecorator="%"
                    fontFamily="Roboto"
                    decimalDigits={decimalDigits}
                  />
                )}
              </DataValue>
              {variationPctg !== null &&
                (parseFloat(variationPctg) > 0 &&
                !isNaN(parseFloat(variationPctg)) ? (
                  <FontAwesomeIconStyled
                    value={variationPctg}
                    icon={faArrowUp}
                  />
                ) : (
                  iff(
                    parseFloat(variationPctg) < 0 &&
                      !isNaN(parseFloat(variationPctg)),
                    <FontAwesomeIconStyled
                      value={variationPctg}
                      icon={faArrowDown}
                    />,
                    <FontAwesomeIconStyled
                      value={
                        !isNaN(parseFloat(variationPctg)) ? variationPctg : 0
                      }
                      icon={faMinus}
                    />,
                  )
                ))}
            </Box>
          </WrapperDataInfo>
        </WrapperDataColumn>
      </WrapperDataRow>
      {!isFunds && (
        <WrapperDataRow isVariationValues>
          <WrapperDataColumn>
            <WrapperDataInfo isVariation>
              <DataLabel>
                {TrlFormattedMessagePlain({
                  id: 'TRLT_UI-MINIMUM',
                })}
              </DataLabel>
              <DataValue>
                <TrlNumberFormatted
                  value={lowPx}
                  fontFamily="Roboto"
                  decimalDigits={decimalDigits}
                />
              </DataValue>
            </WrapperDataInfo>
            <WrapperDataInfo isVariation>
              <DataLabel>
                {TrlFormattedMessagePlain({
                  id: 'TRLT_UI-MAXIMUM',
                })}
              </DataLabel>
              <DataValue>
                <TrlNumberFormatted
                  value={highPx}
                  fontFamily="Roboto"
                  decimalDigits={decimalDigits}
                />
              </DataValue>
            </WrapperDataInfo>
          </WrapperDataColumn>
          <WrapperDataColumn>
            <WrapperDataInfo isVariation>
              <DataLabel>
                {TrlFormattedMessagePlain({
                  id: 'TRLT_UI-PURCHASE',
                })}
              </DataLabel>
              <DataValue>
                <TrlNumberFormatted
                  value={bidPx}
                  fontFamily="Roboto"
                  decimalDigits={decimalDigits}
                />
              </DataValue>
            </WrapperDataInfo>
            <WrapperDataInfo isVariation>
              <DataLabel>
                {TrlFormattedMessagePlain({
                  id: 'TRLT_UI-SALE',
                })}
              </DataLabel>
              <DataValue>
                <TrlNumberFormatted
                  value={askPx}
                  fontFamily="Roboto"
                  decimalDigits={decimalDigits}
                />
              </DataValue>
            </WrapperDataInfo>
          </WrapperDataColumn>
        </WrapperDataRow>
      )}
    </WrapperDataRow>
  );

  return (
    <Wrapper variationPctg={variationPctg}>
      <TrlMainDetails
        instrument={instrument}
        symbol={symbol}
        exchange={exchange || securityExchange}
        securityType={securityType}
        country={country}
        exchangeDescr={exchangeDescr}
        currency={currency}
        descr={descr || securityDescription}
        priceRepresentation={priceRepresentation}
        favourite={isFavourite}
      />
      {showSwiper && !isFunds ? (
        <Swiper
          loop
          pagination={{
            clickable: true,
          }}
          modules={[Pagination]}
        >
          <SwiperSlide>{getMiddleLeftContent()}</SwiperSlide>
          <SwiperSlide>{getMiddleRightContent()}</SwiperSlide>
        </Swiper>
      ) : (
        <>
          {getMiddleLeftContent()}
          {getMiddleRightContent()}
        </>
      )}

      {matches && (
        <WrapperDataRow isButton>
          <WrapperButtons>
            <ButtonMinus
              disabled={!isTitleNegotiable}
              onClick={() => {
                openInsertOrderModal(isFunds ? 'E' : 2);
              }}
            />
            <ButtonPlus
              disabled={!isTitleNegotiable}
              onClick={() => {
                openInsertOrderModal(isFunds ? 'D' : 1);
              }}
            />
          </WrapperButtons>
        </WrapperDataRow>
      )}

      <ButtonExpand id="btnExpandStockExchangeDet" {...props} />
    </Wrapper>
  );
};

TrlCardStockExchangeDet.propTypes = {
  selectedItem: PropTypes.object,
  isFunds: PropTypes.bool,
};
